import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { PortalFileRelease, PortalReleaseBundle, ProjectFileStream } from '../types';
import { mergeBundlesWithReleases } from './portalFileReleaseService';

export interface PortalSupplementarySection {
  streamId: string;
  stream: ProjectFileStream;
  releases: PortalFileRelease[];
  bundles: PortalReleaseBundle[];
}

export async function loadSupplementaryStreamSections(
  releases: PortalFileRelease[],
  bundles: PortalReleaseBundle[],
): Promise<PortalSupplementarySection[]> {
  const streamIds = Array.from(new Set(releases.map(r => r.sourceStreamId).filter(Boolean)));
  const sections: PortalSupplementarySection[] = [];

  for (const streamId of streamIds) {
    try {
      const snap = await getDoc(doc(db, 'projectFileStreams', streamId));
      if (!snap.exists()) continue;
      const d = snap.data();
      if (d.streamRole !== 'supplementary') continue;
      const stream: ProjectFileStream = {
        id: snap.id,
        projectId: String(d.projectId || ''),
        title: String(d.title || 'Untitled'),
        streamKind: d.streamKind as ProjectFileStream['streamKind'],
        description: typeof d.description === 'string' ? d.description : undefined,
        createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
        createdBy: String(d.createdBy || ''),
        updatedAt: (d.updatedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
        streamRole: 'supplementary',
        portalDisplayName: typeof d.portalDisplayName === 'string' ? d.portalDisplayName : undefined,
      };
      const streamReleases = releases.filter(r => r.sourceStreamId === streamId);
      if (streamReleases.length === 0) continue;
      const streamBundles = mergeBundlesWithReleases(
        bundles.filter(b => streamReleases.some(r => b.releaseIds.includes(r.id!))),
        streamReleases,
      );
      sections.push({
        streamId,
        stream,
        releases: streamReleases,
        bundles: streamBundles,
      });
    } catch {
      /* skip unreadable stream */
    }
  }

  return sections.sort((a, b) =>
    (a.stream.portalDisplayName || a.stream.title).localeCompare(
      b.stream.portalDisplayName || b.stream.title,
    ),
  );
}
